import React from 'react';
import {
    DisplayDate,
    DisplayDefault,
    DisplayEmail,
    DisplayFile,
    DisplayImage,
    DisplayRadio,
    DisplayRichText,
    DisplayTag,
    DisplayUrl,
} from './components/DisplayRender';
import {
    FieldConfiguration,
    WidgetCheckbox,
    WidgetOrientedlinks,
    WidgetRadio,
    WidgetSelect,
    WidgetTaxonomy,
    WidgetUri,
} from 'jamespot-user-api';
import { InputDate, SearchDate } from './components/inputs/Date';
import { DefaultInput } from './components/inputs/DefaultInput';
import { InputTextarea } from './components/inputs/InputTextarea';
import { OrientedLinkInput } from './components/inputs/OrientedLink';
import { InputRichText } from './components/inputs/InputRichText';
import { InputSelect } from './components/inputs/InputSelect';
import { widgetToOptions } from './displayer.utils';
import { InputEmail } from './components/inputs/InputEmail';
import { InputTaxonomy } from './components/inputs/InputTaxonomy';
import { DisplayerElement } from './types';
import type { FieldValues, FieldPath } from 'react-hook-form';
import { InputAudience } from './components/inputs/InputAudience';
import { InputCheckbox } from './components/inputs/InputCheckbox';
import { InputFile } from './components/inputs/InputFile';
import { InputImage } from './components/inputs/InputImage';

function NotImplemented() {
    return <></>;
}

const defaultComponents = {
    render: NotImplemented,
    input: NotImplemented,
    search: NotImplemented,
};

export function formatAudience<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: InputAudience,
        },
    };
}

export function formatCheckbox<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: InputCheckbox(widgetToOptions(configuration.widget as WidgetCheckbox), {
                checkboxMode: 'checkbox',
            }),
        },
    };
}

export function formatDate<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDate,
            input: InputDate,
            search: SearchDate,
        },
    };
}

export function formatDefault<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: DefaultInput,
        },
    };
}

export function formatEmail<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayEmail,
            input: InputEmail,
        },
    };
}

export function formatFile<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayFile,
            input: InputFile,
        },
    };
}

export function formatImage<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayImage,
            input: InputImage,
        },
    };
}

export function formatNumber<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: DefaultInput,
        },
    };
}

export function formatOrientedlinks<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayTag,
            input: OrientedLinkInput((configuration.widget as WidgetOrientedlinks).params.supportedTypes?.[0]),
            search: OrientedLinkInput((configuration.widget as WidgetOrientedlinks).params.supportedTypes?.[0], false),
        },
    };
}

export function formatRadio<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayRadio(widgetToOptions(configuration.widget as WidgetRadio)),
            input: InputCheckbox(widgetToOptions(configuration.widget as WidgetRadio), {
                checkboxMode: 'radio',
            }),
            search: InputCheckbox(widgetToOptions(configuration.widget as WidgetRadio), {
                checkboxMode: 'radio',
                direction: 'column',
                requireable: false,
            }),
        },
    };
}

export function formatSelect<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: InputSelect(widgetToOptions(configuration.widget as WidgetSelect), {
                name: configuration.name,
                placeholder: configuration.label,
            }),
        },
    };
}

export function formatRichText<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayRichText,
            input: InputRichText,
        },
    };
}

export function formatTaxonomy<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: InputTaxonomy<TFieldValues>(configuration.widget as WidgetTaxonomy, {
                name: configuration.name,
                placeholder: configuration.label,
                type: configuration.widget.type,
                limit: (configuration.widget as WidgetTaxonomy).params.limit,
            }),
        },
    };
}

export function formatTextarea<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: InputTextarea,
        },
    };
}

export function formatToggle<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: InputCheckbox([{ label: configuration.label, value: 1 }], { checkboxMode: 'toggle' }),
        },
    };
}

export function formatUri<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayDefault,
            input: OrientedLinkInput((configuration.widget as WidgetUri).params.type),
        },
    };
}

export function formatUrl<TFieldValues extends FieldValues, Properties extends FieldPath<TFieldValues>>(
    configuration: FieldConfiguration<Properties>,
): DisplayerElement<TFieldValues> {
    return {
        ...configuration,
        components: {
            ...defaultComponents,
            render: DisplayUrl,
        },
    };
}
